import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getSiteOrigin, joinUrl } from '../utils/seo';
import { useAuth } from '../auth/authStore';
import { psychologistsApi } from '../api/psychologistsApi';
import { sessionsApi } from '../api/sessionsApi';
import { dictApi } from '../api/dictApi';

const TABS = {
  ALL: 'all',
  GROUPS: 'groups',
  PSYCHOLOGISTS: 'psychs',
};

const FILTER_CONFIG = [
  {
    id: 'theme',
    label: 'Тема',
    options: [],
  },
  {
    id: 'method',
    label: 'Метод',
    options: [],
  },
  {
    id: 'experience',
    label: 'Опыт',
    options: ['До 3 лет', '3-7 лет', '8-12 лет', '13+ лет'],
  },
  {
    id: 'price',
    label: 'Цена',
    options: ['До 3 000 ₽', '3 000–4 000 ₽', '4 000–5 000 ₽', 'От 5 000 ₽'],
  },
];

const EMPTY_FILTERS = {
  theme: '',
  method: '',
  experience: '',
  price: '',
};

const CATALOG_PAGE_SIZE = 8;
const CARD_PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop stop-color='%23d7d8ff'/%3E%3Cstop offset='1' stop-color='%23f6f7ff'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23g)'/%3E%3C/svg%3E";

function normalizeTab(value) {
  if (value === TABS.GROUPS || value === TABS.PSYCHOLOGISTS || value === TABS.ALL) {
    return value;
  }
  return TABS.ALL;
}

function spotsClass(spotsTaken, spotsTotal) {
  if (!spotsTotal || spotsTaken >= spotsTotal) return 'full';
  const ratio = spotsTaken / spotsTotal;
  if (ratio >= 0.65) return 'few';
  return 'ok';
}

function shortDateLabel(dateLabel) {
  return String(dateLabel || '').split('.').slice(0, 2).join('.') || dateLabel;
}

function matchExperienceFilter(years, filterValue) {
  if (!filterValue) return true;
  if (filterValue === 'До 3 лет') return years <= 3;
  if (filterValue === '3-7 лет') return years >= 3 && years <= 7;
  if (filterValue === '8-12 лет') return years >= 8 && years <= 12;
  if (filterValue === '13+ лет') return years >= 13;
  return true;
}

function formatYearsRu(value) {
  const years = Math.abs(Number(value));
  if (!Number.isFinite(years) || years <= 0) return '—';
  const mod10 = years % 10;
  const mod100 = years % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${years} лет`;
  if (mod10 === 1) return `${years} год`;
  if (mod10 >= 2 && mod10 <= 4) return `${years} года`;
  return `${years} лет`;
}

function matchPriceFilter(priceValue, filterValue) {
  if (!filterValue) return true;
  if (filterValue === 'До 3 000 ₽') return priceValue < 3000;
  if (filterValue === '3 000–4 000 ₽') return priceValue >= 3000 && priceValue <= 4000;
  if (filterValue === '4 000–5 000 ₽') return priceValue > 4000 && priceValue <= 5000;
  if (filterValue === 'От 5 000 ₽') return priceValue >= 5000;
  return true;
}

function safeText(v) {
  return String(v ?? '').trim();
}

function normalizeComparable(v) {
  return safeText(v).toLowerCase();
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function extractPageContent(raw) {
  if (Array.isArray(raw?.content)) return raw.content;
  if (Array.isArray(raw?.items?.content)) return raw.items.content;
  if (Array.isArray(raw?.item?.content)) return raw.item.content;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.item)) return raw.item;
  if (Array.isArray(raw)) return raw;
  return [];
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.item)) return raw.item;
  if (Array.isArray(raw?.content)) return raw.content;
  return [];
}

function extractTotalElements(raw, fallback = 0) {
  const n =
    Number(raw?.totalElements) ||
    Number(raw?.items?.totalElements) ||
    Number(raw?.item?.totalElements);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function normalizeLabelList(values) {
  const out = [];
  toArray(values).forEach((item) => {
    if (!item) return;
    if (typeof item === 'string') {
      const t = safeText(item);
      if (t) out.push(t);
      return;
    }
    const t = safeText(item?.title || item?.name || item?.label || item?.value);
    if (t) out.push(t);
  });
  return Array.from(new Set(out));
}

function splitLabelTokens(values) {
  const tokens = normalizeLabelList(values)
    .flatMap((value) => String(value).split(/[,\n;•]+/g))
    .map((value) => safeText(value))
    .filter(Boolean);
  return Array.from(new Set(tokens));
}

function formatMoneyLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 'По запросу';
  return `${n.toLocaleString('ru-RU')} ₽`;
}

function toDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateLabel(value) {
  const d = toDate(value);
  if (!d) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function formatTimeLabel(value) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function formatRangeLabel(startIso, endIso) {
  const start = toDate(startIso);
  const end = toDate(endIso);
  if (!start) return '';
  const from = formatTimeLabel(startIso);
  const to = end ? formatTimeLabel(endIso) : '';
  return to ? `${from}–${to}` : from;
}

function parseDurationMinutes(value) {
  if (value == null || value === '') return 0;

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
  }

  const text = safeText(value);
  if (!text) return 0;

  const numeric = Number(text);
  if (Number.isFinite(numeric) && numeric > 0) {
    return Math.round(numeric);
  }

  const hhmmss = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hhmmss) {
    const hours = Number(hhmmss[1]) || 0;
    const mins = Number(hhmmss[2]) || 0;
    return hours * 60 + mins;
  }

  const iso = text.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i);
  if (iso) {
    const hours = Number(iso[1] || 0);
    const mins = Number(iso[2] || 0);
    const secs = Number(iso[3] || 0);
    return hours * 60 + mins + (secs >= 30 ? 1 : 0);
  }

  return 0;
}

function resolveDurationMinutes(raw) {
  if (!raw || typeof raw !== 'object') return 0;

  const candidates = [
    raw?.sessionDurationMinutes,
    raw?.session_duration_minutes,
    raw?.sessionDuration,
    raw?.durationMinutes,
    raw?.duration_minutes,
    raw?.booking?.sessionDurationMinutes,
    raw?.booking?.session_duration_minutes,
    raw?.booking?.sessionDuration,
    raw?.booking?.durationMinutes,
    raw?.booking?.duration_minutes,
    raw?.booking?.slotMinutes,
    raw?.booking?.slot_minutes,
    raw?.schedule?.slotMinutes,
    raw?.schedule?.slot_minutes,
    raw?.flexibleSchedule?.slotMinutes,
    raw?.flexibleSchedule?.slot_minutes,
  ];

  for (const value of candidates) {
    const minutes = parseDurationMinutes(value);
    if (minutes > 0) return minutes;
  }

  return 0;
}

function normalizeSlotsFromApi(rawSlots) {
  const parsed = [];
  toArray(rawSlots).forEach((slot) => {
    if (!slot) return;
    if (typeof slot === 'string') {
      const iso = toDate(slot);
      if (iso) {
        const day = iso.toLocaleDateString('ru-RU', { weekday: 'short' }).replace('.', '');
        const time = iso.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        parsed.push(`${day} ${time}`);
      } else {
        parsed.push(safeText(slot));
      }
      return;
    }
    const start = slot?.startAt || slot?.startDateTime || slot?.start || slot?.dateTime || slot?.datetime;
    if (start) {
      const dt = toDate(start);
      if (dt) {
        const day = dt.toLocaleDateString('ru-RU', { weekday: 'short' }).replace('.', '');
        const time = dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        parsed.push(`${day} ${time}`);
        return;
      }
    }
    const dayLabel = safeText(slot?.day || slot?.dayLabel || slot?.date || '');
    const timeLabel = safeText(slot?.time || slot?.timeLabel || '');
    const joined = safeText(`${dayLabel} ${timeLabel}`);
    if (joined) parsed.push(joined);
  });

  const uniq = Array.from(new Set(parsed.map((x) => safeText(x)).filter(Boolean)));
  return uniq.slice(0, 2);
}

function parsePriceRangeLabel(label) {
  const text = safeText(label).replace(/\u00A0/g, ' ');
  if (!text) return { minPrice: null, maxPrice: null };
  const nums = (text.match(/\d[\d\s]*/g) || [])
    .map((item) => Number(item.replace(/\s+/g, '')))
    .filter((n) => Number.isFinite(n));
  if (!nums.length) return { minPrice: null, maxPrice: null };
  if (text.includes('До')) return { minPrice: null, maxPrice: nums[0] };
  if (text.includes('От') && nums.length === 1) return { minPrice: nums[0], maxPrice: null };
  if (nums.length >= 2) {
    return { minPrice: Math.min(nums[0], nums[1]), maxPrice: Math.max(nums[0], nums[1]) };
  }
  return { minPrice: null, maxPrice: null };
}

function firstLabelFrom(values, fallback = '') {
  const arr = normalizeLabelList(values);
  return arr[0] || fallback;
}

function mapGroupCard(raw, fallbackIndex = 0) {
  const themes = splitLabelTokens(raw?.themes || raw?.themeLabels || raw?.topics);
  const methods = splitLabelTokens(raw?.methods || raw?.methodLabels || raw?.method);
  const priceValue = Number(raw?.priceAtTime ?? raw?.price ?? raw?.priceValue ?? 0) || 0;
  const spotsTaken = Number(raw?.participantsCount ?? raw?.paidCount ?? raw?.spotsTaken ?? 0) || 0;
  const spotsTotal = Number(raw?.capacityClients ?? raw?.spotsTotal ?? raw?.capacity ?? 0) || 0;

  const startDateTime = raw?.startDateTime || raw?.start_datetime || raw?.start || null;
  const endDateTime = raw?.endDateTime || raw?.end_datetime || raw?.end || null;

  const dateLabel = formatDateLabel(startDateTime) || safeText(raw?.dateLabel || raw?.date || '');
  const timeLabel = formatRangeLabel(startDateTime, endDateTime) || safeText(raw?.timeLabel || raw?.time || '');
  const method = firstLabelFrom(methods, safeText(raw?.method));
  const topic = firstLabelFrom(themes, safeText(raw?.topic));
  const durationMinutes = resolveDurationMinutes(raw);

  return {
    id: raw?.id ?? raw?.sessionId ?? `group-${fallbackIndex}`,
    title: safeText(raw?.title || raw?.name),
    host: safeText(raw?.psychologist?.name || raw?.psychologistName || raw?.host),
    topic,
    dateLabel,
    timeLabel,
    spotsTaken,
    spotsTotal,
    priceLabel: formatMoneyLabel(priceValue),
    priceValue,
    method: safeText(method),
    hostExperienceYears: Number(raw?.psychologist?.experienceYears ?? raw?.hostExperienceYears ?? 0) || 0,
    themes,
    durationMinutes,
    imageUrl: safeText(raw?.coverImg || raw?.coverImageUrl || raw?.imageUrl || raw?.previewImageUrl || ''),
    description: safeText(raw?.description || ''),
    dateOptions: toArray(raw?.dateOptions).map((row) => ({
      dayLabel: safeText(row?.dayLabel || row?.dateLabel || row?.date),
      timeLabel: safeText(row?.timeLabel || row?.time || row?.range),
      spotsLeft: Number(row?.spotsLeft ?? row?.availableSpots ?? row?.freeSpots ?? 0) || 0,
    })),
  };
}

function mapPsychologistCard(raw, fallbackIndex = 0) {
  const themes = splitLabelTokens(raw?.themes || raw?.themeLabels || raw?.topics);
  const methods = splitLabelTokens(raw?.methods || raw?.methodLabels || raw?.method);
  const method = firstLabelFrom(methods, safeText(raw?.method));
  const experienceYears = Number(raw?.experienceYears ?? raw?.expYears ?? 0) || 0;
  const priceValue = Number(raw?.pricePerSession ?? raw?.price ?? raw?.priceValue ?? 0) || 0;
  const duration = resolveDurationMinutes(raw);
  const rating = Number(raw?.averageRating ?? raw?.rating ?? raw?.ratingValue);
  const reviews = Number(raw?.reviewsCount ?? raw?.reviews ?? raw?.feedbackCount);
  const slots = normalizeSlotsFromApi(raw?.nextSlots || raw?.availableSlots || raw?.slots || raw?.calendarPreview);
  const onlineRaw =
    raw?.isOnline ??
    raw?.is_online ??
    raw?.online ??
    raw?.user?.isOnline ??
    raw?.user?.is_online ??
    raw?.user?.online ??
    null;
  const hasOnlineStatus = onlineRaw !== null && onlineRaw !== undefined && String(onlineRaw).trim() !== '';
  const isOnline = hasOnlineStatus ? Boolean(onlineRaw) : null;

  return {
    id: raw?.id ?? raw?.userId ?? `psy-${fallbackIndex}`,
    userId: raw?.userId ?? raw?.id ?? null,
    name: safeText(raw?.name),
    statusLabel: hasOnlineStatus ? (isOnline ? 'Онлайн' : 'Офлайн') : 'Статус не указан',
    isOnline,
    ratingLabel: Number.isFinite(rating) && rating > 0 ? rating.toFixed(1) : '—',
    reviewsLabel: Number.isFinite(reviews) && reviews >= 0 ? String(reviews) : '0',
    specializationLabel: [method, experienceYears > 0 ? `${experienceYears}+ лет` : '']
      .filter(Boolean)
      .join(' · '),
    slots,
    priceLabel: formatMoneyLabel(priceValue),
    priceValue,
    experienceYears,
    method,
    methods,
    themes,
    durationLabel: duration > 0 ? `/ ${duration} мин` : '',
    durationMinutes: duration,
    imageUrl: safeText(raw?.avatarUrl || raw?.imageUrl || raw?.photoUrl || ''),
    about: safeText(raw?.about || raw?.headline || ''),
    isFavorite: Boolean(raw?.isFavorite),
    averageRating: Number.isFinite(rating) ? rating : null,
    reviewsCount: Number.isFinite(reviews) ? reviews : 0,
  };
}

function buildSeoPayload(defaultTab, canonicalPath) {
  if (defaultTab === TABS.PSYCHOLOGISTS) {
    return {
      title: 'Психологи — каталог специалистов | Бюро Души',
      description: 'Найдите психолога по теме, методу и стоимости. Подбор специалиста и запись на индивидуальные сессии.',
      schemaName: 'Каталог психологов',
      canonicalPath,
    };
  }

  if (defaultTab === TABS.GROUPS) {
    return {
      title: 'Групповые сессии — каталог и запись | Бюро Души',
      description: 'Актуальные групповые сессии: даты, ведущие, стоимость и свободные места. Выберите формат и запишитесь онлайн.',
      schemaName: 'Каталог групповых сессий',
      canonicalPath,
    };
  }

  return {
    title: 'Каталог специалистов и сессий | Бюро Души',
    description: 'Единый каталог Бюро Души: индивидуальные психологи и групповые сессии в одном разделе.',
    schemaName: 'Каталог специалистов и сессий',
    canonicalPath,
  };
}

function HeartIcon({ active = false }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
        fill={active ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function toFavoriteKey(id) {
  return String(id ?? '');
}

function getGroupDateOptions(item) {
  if (!item) return [];
  if (Array.isArray(item.dateOptions) && item.dateOptions.length) {
    return item.dateOptions
      .map((row) => ({
        dayLabel: safeText(row?.dayLabel || row?.dateLabel || row?.date),
        timeLabel: safeText(row?.timeLabel || row?.time || row?.range),
        spotsLeft: Number(row?.spotsLeft ?? row?.availableSpots ?? row?.freeSpots ?? 0) || 0,
      }))
      .filter((row) => row.dayLabel && row.timeLabel);
  }

  const left = Math.max(0, Number(item.spotsTotal || 0) - Number(item.spotsTaken || 0));
  if (!item.dateLabel || !item.timeLabel) return [];
  return [{ dayLabel: item.dateLabel, timeLabel: item.timeLabel, spotsLeft: left }];
}

function getPsychSlots(item) {
  return (item?.slots || [])
    .filter((slot) => String(slot).includes(':'))
    .map((slot) => {
      const parts = String(slot).trim().split(/\s+/);
      const timeLabel = parts.pop() || '';
      const dayLabel = parts.join(' ') || 'Ближайший';
      return {
        key: `${dayLabel}-${timeLabel}`,
        dayLabel,
        timeLabel,
        fullLabel: `${dayLabel}, ${timeLabel}`,
      };
    });
}

function spotsLeftClass(spotsLeft) {
  if (spotsLeft <= 0) return 'msfull';
  if (spotsLeft <= 3) return 'msfew';
  return 'msok';
}

function totalPagesFor(totalItems, pageSize) {
  const safeSize = Math.max(1, Number(pageSize) || 1);
  return Math.max(1, Math.ceil(Math.max(0, Number(totalItems) || 0) / safeSize));
}

function clampPage(value, totalPages) {
  const n = Number(value) || 1;
  const max = Math.max(1, Number(totalPages) || 1);
  return Math.min(max, Math.max(1, n));
}

function buildPageNumbers(currentPage, totalPages) {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  const normalized = Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const out = [];
  for (let i = 0; i < normalized.length; i += 1) {
    const page = normalized[i];
    const prev = normalized[i - 1];
    if (i > 0 && page - prev > 1) out.push('...');
    out.push(page);
  }
  return out;
}

export default function Catalog({ defaultTab = TABS.ALL, canonicalPath = '/catalog' }) {
  const auth = useAuth();
  const isAuthenticated =
    typeof auth?.isAuthenticated === 'boolean'
      ? auth.isAuthenticated
      : Boolean(auth?.token ?? auth?.me);

  const [activeTab, setActiveTab] = useState(normalizeTab(defaultTab));
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_FILTERS);
  const [openFilterId, setOpenFilterId] = useState('');
  const [groupsData, setGroupsData] = useState([]);
  const [psychologistsData, setPsychologistsData] = useState([]);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [psychTotal, setPsychTotal] = useState(0);
  const [dictThemes, setDictThemes] = useState([]);
  const [dictMethods, setDictMethods] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingPsychologists, setLoadingPsychologists] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [psychError, setPsychError] = useState('');
  const [favoriteGroupIds, setFavoriteGroupIds] = useState(() => new Set());
  const [favoritePsychIds, setFavoritePsychIds] = useState(() => new Set());
  const [modalState, setModalState] = useState(null);
  const [selectedModalSlot, setSelectedModalSlot] = useState('');
  const [selectedModalDate, setSelectedModalDate] = useState('');
  const [groupsPage, setGroupsPage] = useState(1);
  const [psychologistsPage, setPsychologistsPage] = useState(1);
  const filtersRef = useRef(null);
  const loadSeqRef = useRef(0);

  useEffect(() => {
    setActiveTab(normalizeTab(defaultTab));
  }, [defaultTab]);

  useEffect(() => {
    if (!openFilterId) return undefined;

    const onDocumentClick = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setOpenFilterId('');
      }
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenFilterId('');
      }
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [openFilterId]);

  const seo = useMemo(() => buildSeoPayload(normalizeTab(defaultTab), canonicalPath), [defaultTab, canonicalPath]);
  const origin = useMemo(() => getSiteOrigin(), []);
  const canonical = useMemo(() => joinUrl(origin, seo.canonicalPath), [origin, seo.canonicalPath]);
  const normalizedQuery = searchValue.trim();
  const normalizedQueryLower = normalizedQuery.toLowerCase();

  const selectedThemeDict = useMemo(
    () =>
      (dictThemes || []).find(
        (item) => safeText(item?.title).toLowerCase() === safeText(selectedFilters.theme).toLowerCase()
      ) || null,
    [dictThemes, selectedFilters.theme]
  );

  const selectedMethodDict = useMemo(
    () =>
      (dictMethods || []).find(
        (item) => safeText(item?.title).toLowerCase() === safeText(selectedFilters.method).toLowerCase()
      ) || null,
    [dictMethods, selectedFilters.method]
  );

  const filterConfig = useMemo(() => {
    const themeOptions = Array.from(new Set(dictThemes.map((item) => safeText(item?.title)).filter(Boolean)));
    const methodOptions = Array.from(new Set(dictMethods.map((item) => safeText(item?.title)).filter(Boolean)));

    return FILTER_CONFIG.map((item) => {
      if (item.id === 'theme') return { ...item, options: themeOptions };
      if (item.id === 'method') return { ...item, options: methodOptions };
      return item;
    });
  }, [dictMethods, dictThemes]);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([dictApi.themes(), dictApi.methods()])
      .then(([themesResult, methodsResult]) => {
        if (!alive) return;
        const themes =
          themesResult.status === 'fulfilled'
            ? extractItems(themesResult.value)
                .map((x) => (x && typeof x === 'object' ? x : null))
                .filter(Boolean)
            : [];
        const methods =
          methodsResult.status === 'fulfilled'
            ? extractItems(methodsResult.value)
                .map((x) => (x && typeof x === 'object' ? x : null))
                .filter(Boolean)
            : [];
        if (themes.length) setDictThemes(themes);
        if (methods.length) setDictMethods(methods);
      })
      .catch(() => {
        setDictThemes([]);
        setDictMethods([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const seq = ++loadSeqRef.current;
    const priceRange = parsePriceRangeLabel(selectedFilters.price);

    async function loadPsychologists() {
      setLoadingPsychologists(true);
      try {
        const response = await psychologistsApi.list({
          page: 0,
          size: 60,
          q: normalizedQuery,
          themes: selectedThemeDict?.id ? [Number(selectedThemeDict.id)] : [],
          methods: selectedMethodDict?.id ? [Number(selectedMethodDict.id)] : [],
          experience: selectedFilters.experience ? [selectedFilters.experience] : [],
          price: selectedFilters.price ? [selectedFilters.price] : [],
          sort: 'rating,desc',
        });
        if (!alive || seq !== loadSeqRef.current) return;
        const content = extractPageContent(response);
        const mapped = content.map((item, idx) => mapPsychologistCard(item, idx));
        setPsychologistsData(mapped.length ? mapped : []);
        setPsychTotal(extractTotalElements(response, mapped.length));
        setPsychError('');
      } catch {
        if (!alive || seq !== loadSeqRef.current) return;
        setPsychologistsData([]);
        setPsychTotal(0);
        setPsychError('Не удалось загрузить каталог специалистов.');
      } finally {
        if (alive && seq === loadSeqRef.current) setLoadingPsychologists(false);
      }
    }

    async function loadGroups() {
      setLoadingGroups(true);
      try {
        const response = await sessionsApi.listPublicGroupSessions({
          page: 0,
          size: 60,
          q: normalizedQuery,
          themeIds: selectedThemeDict?.id ? [Number(selectedThemeDict.id)] : [],
          methodIds: selectedMethodDict?.id ? [Number(selectedMethodDict.id)] : [],
          minPrice: priceRange.minPrice,
          maxPrice: priceRange.maxPrice,
        });
        if (!alive || seq !== loadSeqRef.current) return;
        const content = extractPageContent(response);
        const mapped = content.map((item, idx) => mapGroupCard(item, idx));
        setGroupsData(mapped.length ? mapped : []);
        setGroupsTotal(extractTotalElements(response, mapped.length));
        setGroupsError('');
      } catch {
        if (!alive || seq !== loadSeqRef.current) return;
        setGroupsData([]);
        setGroupsTotal(0);
        setGroupsError('Не удалось загрузить групповые сессии.');
      } finally {
        if (alive && seq === loadSeqRef.current) setLoadingGroups(false);
      }
    }

    loadPsychologists();
    loadGroups();

    return () => {
      alive = false;
    };
  }, [
    normalizedQuery,
    selectedFilters.experience,
    selectedFilters.method,
    selectedFilters.price,
    selectedFilters.theme,
    selectedMethodDict?.id,
    selectedThemeDict?.id,
  ]);

  useEffect(() => {
    setGroupsPage(1);
    setPsychologistsPage(1);
  }, [normalizedQuery, selectedFilters.experience, selectedFilters.method, selectedFilters.price, selectedFilters.theme]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    psychologistsApi
      .favourites()
      .then((ids) => {
        if (!alive) return;
        const next = new Set(
          extractItems(ids)
            .map((id) => toFavoriteKey(id))
            .filter(Boolean)
        );
        setFavoritePsychIds(next);
      })
      .catch(() => {
        // keep local state
      });

    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const fromPayload = psychologistsData
      .filter((item) => item?.isFavorite)
      .map((item) => toFavoriteKey(item.userId || item.id))
      .filter(Boolean);
    if (!fromPayload.length) return;
    setFavoritePsychIds((prev) => {
      const next = new Set(prev);
      fromPayload.forEach((id) => next.add(id));
      return next;
    });
  }, [psychologistsData]);

  const filteredGroups = useMemo(() => {
    return groupsData.filter((item) => {
      const selectedThemeNormalized = normalizeComparable(selectedFilters.theme);
      const selectedMethodNormalized = normalizeComparable(selectedFilters.method);
      const queryMatched = !normalizedQuery
        || [item.title, item.host, item.topic, item.method, ...(item.themes || [])]
          .join(' ')
          .toLowerCase()
          .includes(normalizedQueryLower);

      if (!queryMatched) return false;

      if (selectedThemeNormalized) {
        const themes = (item.themes || []).map((value) => normalizeComparable(value));
        if (!themes.includes(selectedThemeNormalized)) return false;
      }

      if (selectedMethodNormalized) {
        const methodCandidates = [item.method, ...(item.methods || [])]
          .map((value) => normalizeComparable(value))
          .filter(Boolean);
        if (!methodCandidates.includes(selectedMethodNormalized)) return false;
      }

      if (!matchExperienceFilter(item.hostExperienceYears || 0, selectedFilters.experience)) return false;
      if (!matchPriceFilter(item.priceValue || 0, selectedFilters.price)) return false;

      return true;
    });
  }, [groupsData, normalizedQuery, normalizedQueryLower, selectedFilters]);

  const filteredPsychologists = useMemo(() => {
    return psychologistsData.filter((item) => {
      const selectedThemeNormalized = normalizeComparable(selectedFilters.theme);
      const selectedMethodNormalized = normalizeComparable(selectedFilters.method);
      const queryMatched = !normalizedQuery
        || [item.name, item.specializationLabel, item.statusLabel, item.method, ...(item.themes || [])]
          .join(' ')
          .toLowerCase()
          .includes(normalizedQueryLower);

      if (!queryMatched) return false;

      if (selectedThemeNormalized) {
        const themes = (item.themes || []).map((value) => normalizeComparable(value));
        if (!themes.includes(selectedThemeNormalized)) return false;
      }

      if (selectedMethodNormalized) {
        const methodCandidates = [item.method, ...(item.methods || [])]
          .map((value) => normalizeComparable(value))
          .filter(Boolean);
        if (!methodCandidates.includes(selectedMethodNormalized)) return false;
      }

      if (!matchExperienceFilter(item.experienceYears || 0, selectedFilters.experience)) return false;
      if (!matchPriceFilter(item.priceValue || 0, selectedFilters.price)) return false;

      return true;
    });
  }, [psychologistsData, normalizedQuery, normalizedQueryLower, selectedFilters]);

  const hasActiveFilters = useMemo(
    () => Boolean(
      normalizedQuery
      || selectedFilters.theme
      || selectedFilters.method
      || selectedFilters.experience
      || selectedFilters.price
    ),
    [normalizedQuery, selectedFilters]
  );

  const groupsTotalPages = useMemo(
    () => totalPagesFor(filteredGroups.length, CATALOG_PAGE_SIZE),
    [filteredGroups.length]
  );
  const psychTotalPages = useMemo(
    () => totalPagesFor(filteredPsychologists.length, CATALOG_PAGE_SIZE),
    [filteredPsychologists.length]
  );

  useEffect(() => {
    setGroupsPage((prev) => clampPage(prev, groupsTotalPages));
  }, [groupsTotalPages]);

  useEffect(() => {
    setPsychologistsPage((prev) => clampPage(prev, psychTotalPages));
  }, [psychTotalPages]);

  const groupsOffset = (groupsPage - 1) * CATALOG_PAGE_SIZE;
  const psychOffset = (psychologistsPage - 1) * CATALOG_PAGE_SIZE;

  const pagedGroups = useMemo(
    () => filteredGroups.slice(groupsOffset, groupsOffset + CATALOG_PAGE_SIZE),
    [filteredGroups, groupsOffset]
  );

  const pagedPsychologists = useMemo(
    () => filteredPsychologists.slice(psychOffset, psychOffset + CATALOG_PAGE_SIZE),
    [filteredPsychologists, psychOffset]
  );

  const allCount = filteredGroups.length + filteredPsychologists.length;
  const groupsCount = filteredGroups.length;
  const psychCount = filteredPsychologists.length;

  const showGroups = activeTab === TABS.ALL || activeTab === TABS.GROUPS;
  const showPsychologists = activeTab === TABS.ALL || activeTab === TABS.PSYCHOLOGISTS;

  const resultCount = activeTab === TABS.GROUPS ? groupsCount : activeTab === TABS.PSYCHOLOGISTS ? psychCount : allCount;

  const toggleFilterDropdown = (filterId) => {
    setOpenFilterId((prev) => (prev === filterId ? '' : filterId));
  };

  const setFilterValue = (filterId, option) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterId]: prev[filterId] === option ? '' : option,
    }));
  };

  const resetFilterValue = (filterId) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterId]: '',
    }));
  };

  const modalOpen = Boolean(modalState?.item);
  const modalType = modalState?.type || '';
  const modalItem = modalState?.item || null;

  const modalSlots = useMemo(() => {
    if (modalType !== 'psych') return [];
    return getPsychSlots(modalItem);
  }, [modalType, modalItem]);

  const modalDates = useMemo(() => {
    if (modalType !== 'group') return [];
    return getGroupDateOptions(modalItem);
  }, [modalType, modalItem]);

  const modalFavoriteActive = useMemo(() => {
    if (!modalItem) return false;
    if (modalType === 'psych') return favoritePsychIds.has(toFavoriteKey(modalItem.userId || modalItem.id));
    if (modalType === 'group') return favoriteGroupIds.has(toFavoriteKey(modalItem.id));
    return false;
  }, [favoriteGroupIds, favoritePsychIds, modalItem, modalType]);

  const modalDetailLink = useMemo(() => {
    if (!modalItem) return '/catalog';
    if (modalType === 'psych') return `/psychologist/${modalItem.userId || modalItem.id}`;
    if (modalType === 'group') return `/group-sessions/${modalItem.id}`;
    return '/catalog';
  }, [modalItem, modalType]);

  const modalRows = useMemo(() => {
    if (!modalItem) return [];

    if (modalType === 'psych') {
      return [
        { label: 'Время', value: selectedModalSlot || 'Выберите слот ниже' },
        { label: 'Опыт', value: formatYearsRu(modalItem.experienceYears) },
        {
          label: 'Метод',
          tags: toArray(modalItem.methods && modalItem.methods.length ? modalItem.methods : modalItem.method).filter(Boolean),
          tagTone: 'l',
        },
        { label: 'Темы', tags: modalItem.themes || [], tagTone: 'm' },
        { label: 'О себе', value: modalItem.about || '—' },
      ];
    }

    if (modalType === 'group') {
      return [
        { label: 'Ведущий', value: modalItem.host || '—' },
        {
          label: 'Метод',
          tags: toArray(modalItem.methods && modalItem.methods.length ? modalItem.methods : modalItem.method).filter(Boolean),
          tagTone: 'l',
        },
        { label: 'Участники', value: `${modalItem.spotsTaken}/${modalItem.spotsTotal} мест` },
        { label: 'Темы', tags: modalItem.themes || [], tagTone: 'm' },
        { label: 'Описание', value: safeText(modalItem.description) || '—' },
      ];
    }

    return [];
  }, [modalItem, modalType, selectedModalSlot]);

  const modalBadgeLabel = useMemo(() => {
    if (!modalItem) return '';
    const duration = Number(modalItem.durationMinutes ?? 0) || 0;
    if (modalType === 'psych') return duration > 0 ? `Индивидуальная · ${duration} мин` : 'Индивидуальная';
    if (modalType === 'group') return duration > 0 ? `Групповая · ${duration} мин` : 'Групповая';
    return '';
  }, [modalItem, modalType]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onEscape = (event) => {
      if (event.key === 'Escape') {
        setModalState(null);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onEscape);
    };
  }, [modalOpen]);

  const openGroupModal = (item) => {
    const dateOptions = getGroupDateOptions(item);
    const firstAvailable = dateOptions.find((row) => Number(row.spotsLeft || 0) > 0);
    const firstDate = firstAvailable || dateOptions[0] || null;

    setSelectedModalDate(firstDate ? `${firstDate.dayLabel}, ${firstDate.timeLabel}` : '');
    setSelectedModalSlot('');
    setModalState({ type: 'group', item });
  };

  const openPsychModal = (item) => {
    const slots = getPsychSlots(item);
    setSelectedModalSlot(slots[0]?.fullLabel || '');
    setSelectedModalDate('');
    setModalState({ type: 'psych', item });
  };

  const closeModal = () => {
    setModalState(null);
  };

  const toggleGroupFavorite = (groupId) => {
    const key = toFavoriteKey(groupId);
    setFavoriteGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePsychFavorite = async (psychId) => {
    const key = toFavoriteKey(psychId);
    let wasFavorite = false;
    setFavoritePsychIds((prev) => {
      const next = new Set(prev);
      wasFavorite = next.has(key);
      if (wasFavorite) next.delete(key);
      else next.add(key);
      return next;
    });

    if (!isAuthenticated || !psychId) return;

    try {
      if (wasFavorite) await psychologistsApi.removeFavourite(psychId);
      else await psychologistsApi.addFavourite(psychId);
    } catch {
      setFavoritePsychIds((prev) => {
        const next = new Set(prev);
        if (wasFavorite) next.add(key);
        else next.delete(key);
        return next;
      });
    }
  };

  const toggleModalFavorite = () => {
    if (!modalItem) return;
    if (modalType === 'psych') {
      togglePsychFavorite(modalItem.userId || modalItem.id);
      return;
    }
    if (modalType === 'group') {
      toggleGroupFavorite(modalItem.id);
    }
  };

  const onCardKeyDown = (event, onOpen) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  const renderPager = (kind) => {
    const isGroups = kind === 'groups';
    const page = isGroups ? groupsPage : psychologistsPage;
    const totalPages = isGroups ? groupsTotalPages : psychTotalPages;
    const setPage = isGroups ? setGroupsPage : setPsychologistsPage;
    if (totalPages <= 1) return null;

    const pageItems = buildPageNumbers(page, totalPages);
    const canPrev = page > 1;
    const canNext = page < totalPages;

    return (
      <div className="catalog-pager" role="navigation" aria-label={isGroups ? 'Пагинация групповых сессий' : 'Пагинация специалистов'}>
        <button type="button" className="catalog-pager-btn" disabled={!canPrev} onClick={() => canPrev && setPage((prev) => prev - 1)}>
          Назад
        </button>
        <div className="catalog-pager-pages">
          {pageItems.map((item, idx) => (
            item === '...'
              ? <span key={`dots-${kind}-${idx}`} className="catalog-pager-dots">…</span>
              : (
                <button
                  key={`${kind}-page-${item}`}
                  type="button"
                  className={`catalog-pager-page ${item === page ? 'active' : ''}`}
                  onClick={() => setPage(Number(item))}
                >
                  {item}
                </button>
              )
          ))}
        </div>
        <button type="button" className="catalog-pager-btn" disabled={!canNext} onClick={() => canNext && setPage((prev) => prev + 1)}>
          Вперёд
        </button>
      </div>
    );
  };

  return (
    <div className="catalog-page">
      <Helmet prioritizeSeoTags>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="index, follow" />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seo.schemaName,
            url: canonical || undefined,
            inLanguage: 'ru-RU',
          })}
        </script>
      </Helmet>

      <div className="page-header">
        <div className="ph-inner">
          <div className="ph-left">
            <div className="page-badge fu">✦ Каталог специалистов</div>
            <h1 className="page-title fu fu1">Найди своего<br /><span className="l">психолога</span></h1>
            <p className="page-sub fu fu2">Индивидуальные сессии и групповые встречи — всё в одном месте. Выбирай формат, который подходит тебе.</p>
            <div className="ph-stats fu fu2">
              <div className="ph-stat"><span className="ph-val">{psychTotal}</span><span className="ph-label">специалиста</span></div>
              <div className="ph-div" />
              <div className="ph-stat"><span className="ph-val">{groupsTotal}</span><span className="ph-label">групп сейчас</span></div>
              <div className="ph-div" />
              <div className="ph-stat"><span className="ph-val">0%</span><span className="ph-label">комиссии</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="controls">
        <div className="tabs-row">
          <button type="button" className={`tab-btn ${activeTab === TABS.ALL ? 'active' : ''}`} onClick={() => setActiveTab(TABS.ALL)}>
            Все <span className="tab-count">{allCount}</span>
          </button>
          <button type="button" className={`tab-btn ${activeTab === TABS.GROUPS ? 'active' : ''}`} onClick={() => setActiveTab(TABS.GROUPS)}>
            Групповые <span className="tab-count">{groupsCount}</span>
          </button>
          <button type="button" className={`tab-btn ${activeTab === TABS.PSYCHOLOGISTS ? 'active' : ''}`} onClick={() => setActiveTab(TABS.PSYCHOLOGISTS)}>
            Специалисты <span className="tab-count">{psychCount}</span>
          </button>
        </div>

        <div className="search-row">
          <div className="search-wrap">
            <span className="search-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <input className="search-input" placeholder="Имя, метод, тема..." type="text" value={searchValue} onChange={(event) => setSearchValue(event.target.value)} />
          </div>
        </div>

        <div className="filters-row" ref={filtersRef}>
          {filterConfig.map((filterItem) => {
            const selectedValue = selectedFilters[filterItem.id];
            const opened = openFilterId === filterItem.id;
            return (
              <div
                key={filterItem.id}
                className={`filter-control ${opened ? 'open' : ''} ${selectedValue ? 'selected' : ''}`}
              >
                <button
                  type="button"
                  className={`filter-btn ${opened || selectedValue ? 'on' : ''}`}
                  onClick={() => toggleFilterDropdown(filterItem.id)}
                >
                  <span className="filter-btn-label">{filterItem.label}</span>
                  {selectedValue ? <span className="filter-btn-dot" aria-hidden="true" /> : null}
                  <svg className="filter-btn-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>

                {opened ? (
                  <div className="filter-dropdown">
                    <ul className="filter-options-list">
                      {filterItem.options.map((option) => {
                        const selected = selectedValue === option;
                        return (
                          <li key={option}>
                            <button
                              type="button"
                              className={`filter-option ${selected ? 'active' : ''}`}
                              onClick={() => setFilterValue(filterItem.id, option)}
                            >
                              <span>{option}</span>
                              {selected ? <span className="filter-option-check">✓</span> : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    <button
                      type="button"
                      className="filter-reset-btn"
                      onClick={() => resetFilterValue(filterItem.id)}
                      disabled={!selectedValue}
                    >
                      Сбросить
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
          <div className="filter-sep" />
          <span className="results-count">{resultCount} результатов</span>
          <button type="button" className="sort-btn">
            По рейтингу
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className="catalog">
        {psychError ? <div className="m-empty">{psychError}</div> : null}
        {groupsError ? <div className="m-empty">{groupsError}</div> : null}
        {showGroups ? (
          <div id="section-groups">
            <div className="section-label">Групповые сессии</div>
            <div className="groups-grid">
              {loadingGroups && !pagedGroups.length ? (
                <div className="m-empty">Загружаем групповые сессии…</div>
              ) : null}
              {!loadingGroups && !pagedGroups.length ? (
                <div className="m-empty">
                  {hasActiveFilters
                    ? 'По выбранным фильтрам пока нет групповых сессий.'
                    : 'Пока нет доступных групповых сессий.'}
                </div>
              ) : null}
              {pagedGroups.map((item, index) => {
                const spot = spotsClass(item.spotsTaken, item.spotsTotal);
                const isFav = favoriteGroupIds.has(toFavoriteKey(item.id));
                const absoluteIndex = groupsOffset + index;
                const animationClass = absoluteIndex % 4 === 0 ? 'fu' : absoluteIndex % 4 === 1 ? 'fu fu1' : absoluteIndex % 4 === 2 ? 'fu fu2' : 'fu fu3';
                return (
                  <article
                    key={item.id}
                    className={`g-card ${animationClass}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openGroupModal(item)}
                    onKeyDown={(event) => onCardKeyDown(event, () => openGroupModal(item))}
                    aria-label={`Открыть модалку сессии: ${item.title}`}
                  >
                    <div className="g-mob-photo">
                      <img src={item.imageUrl || CARD_PLACEHOLDER_IMAGE} loading="lazy" alt={item.title} />
                      <div className="g-topic">{item.topic}</div>
                    </div>

                    <div className="g-overlay" />
                    <button
                      type="button"
                      className={`g-fav ${isFav ? 'active' : ''}`}
                      aria-label={isFav ? 'Убрать из избранного' : 'Добавить в избранное'}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleGroupFavorite(item.id);
                      }}
                    >
                      <HeartIcon active={isFav} />
                    </button>

                    <div className="g-bottom">
                      <div className="g-title">{item.title}</div>
                      <div className="g-host">Ведущий: {item.host}</div>
                      <div className="g-row">
                        <div className="g-meta">
                          <div className="g-date">{item.dateLabel} · {item.timeLabel}</div>
                          <div className={`g-spots ${spot}`}>{item.spotsTaken}/{item.spotsTotal} мест</div>
                        </div>
                        <div className="g-price">{item.priceLabel}</div>
                      </div>
                    </div>

                    <div className="g-mob-body">
                      <div>
                        <div className="g-title">{item.title}</div>
                        <div className="g-host">{item.host}</div>
                      </div>
                      <div className="g-row">
                        <div className="g-meta">
                          <div className="g-date">{shortDateLabel(item.dateLabel)} · {item.timeLabel}</div>
                          <div className={`g-spots ${spot}`}>{item.spotsTaken}/{item.spotsTotal} мест</div>
                        </div>
                        <div className="g-price">{item.priceLabel}</div>
                      </div>
                    </div>

                  </article>
                );
              })}
            </div>
            {renderPager('groups')}
          </div>
        ) : null}

        {showPsychologists ? (
          <div id="section-psychs">
            <div className="section-label">Специалисты</div>
            <div className="psychs-grid">
              {loadingPsychologists && !pagedPsychologists.length ? (
                <div className="m-empty">Загружаем карточки специалистов…</div>
              ) : null}
              {!loadingPsychologists && !pagedPsychologists.length ? (
                <div className="m-empty">
                  {hasActiveFilters
                    ? 'По выбранным фильтрам пока нет специалистов.'
                    : 'Пока нет доступных специалистов.'}
                </div>
              ) : null}
              {pagedPsychologists.map((item, index) => {
                const favKey = toFavoriteKey(item.userId || item.id);
                const isFav = favoritePsychIds.has(favKey);
                const absoluteIndex = psychOffset + index;
                const animationClass = absoluteIndex % 4 === 0 ? 'fu' : absoluteIndex % 4 === 1 ? 'fu fu1' : absoluteIndex % 4 === 2 ? 'fu fu2' : 'fu fu3';
                return (
                <article
                  key={item.id}
                  className={`p-card ${animationClass}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openPsychModal(item)}
                  onKeyDown={(event) => onCardKeyDown(event, () => openPsychModal(item))}
                  aria-label={`Открыть модалку психолога: ${item.name}`}
                >
                  <img src={item.imageUrl || CARD_PLACEHOLDER_IMAGE} loading="lazy" alt={item.name} />
                  <div className="p-overlay" />

                  <div className="p-top">
                    <div className={`p-status ${item.isOnline === true ? 'p-on' : item.isOnline === false ? 'p-off' : 'p-unk'}`}>
                      {item.isOnline === true ? '● Онлайн' : item.isOnline === false ? '● Офлайн' : '● Статус не указан'}
                    </div>
                    <button
                      type="button"
                      className={`p-fav ${isFav ? 'active' : ''}`}
                      aria-label={isFav ? 'Убрать из избранного' : 'Добавить в избранное'}
                      onClick={(event) => {
                        event.stopPropagation();
                        togglePsychFavorite(item.userId || item.id);
                      }}
                    >
                      <HeartIcon active={isFav} />
                    </button>
                  </div>

                  <div className="p-bottom">
                    <div className="p-slots">
                      {item.slots.map((slotLabel) => (
                        <span key={`${item.id}-${slotLabel}`} className="p-slot">{slotLabel}</span>
                      ))}
                    </div>

                    <div className="p-rating"><span className="p-stars">★★★★★</span><span className="p-rval">{item.ratingLabel}</span><span className="p-rcnt">({item.reviewsLabel})</span></div>
                    <div className="p-name">{item.name}</div>
                    <div className="p-spec">{item.specializationLabel}</div>

                    <div className="p-footer">
                      <div className="p-price">{item.priceLabel}<span className="p-dur">{item.durationLabel}</span></div>
                      <span className="p-btn"><ArrowIcon /></span>
                    </div>
                  </div>

                </article>
                );
              })}
            </div>
            {renderPager('psychologists')}
          </div>
        ) : null}
      </div>

      {modalOpen && modalItem ? (
        <div className="moverlay open" role="dialog" aria-modal="true" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeModal();
        }}
        >
          <div className="mbox">
            <button className="mclose" type="button" onClick={closeModal} aria-label="Закрыть">✕</button>
            <div className="mbody">
              <div className="mleft">
                <div className="mleft-photo" style={{ backgroundImage: `url(${modalItem.imageUrl || ''})` }} />
                <div className="mleft-grad" />
                <div className="mleft-info">
                  <div className="m-name">{modalType === 'psych' ? modalItem.name : modalItem.title}</div>
                  <div className={`m-badge ${modalType === 'psych' ? 'mint' : 'lav'}`}>
                    {modalBadgeLabel}
                  </div>
                  <div className="m-price">
                    {modalItem.priceLabel}
                    <small>{modalType === 'psych' ? 'за сессию' : 'за встречу'}</small>
                  </div>
                </div>
              </div>

              <div className="mright">
                <div className="mdetails">
                  {modalRows.map((row) => (
                    <div key={row.label} className="mrow">
                      <div className="mrlabel">{row.label}</div>
                      <div className={`mrval ${row.tags ? 'tags' : ''}`}>
                        {row.tags
                          ? (row.tags.length
                            ? row.tags.map((tag) => (
                              <span key={`${row.label}-${tag}`} className={`t ${row.tagTone || 'm'}`}>{tag}</span>
                            ))
                            : '—')
                          : row.value}
                      </div>
                    </div>
                  ))}
                </div>

                {modalType === 'psych' ? (
                  <div className="mslots">
                    <div className="msl-label">Выберите слот</div>
                    {modalSlots.length > 0 ? (
                      <div className="msl-grid">
                        {modalSlots.map((slot) => (
                          <button
                            key={slot.key}
                            type="button"
                            className={`msl ${selectedModalSlot === slot.fullLabel ? 'sel' : ''}`}
                            onClick={() => setSelectedModalSlot(slot.fullLabel)}
                          >
                            <div className="msl-time">{slot.timeLabel}</div>
                            <div className="msl-date">{slot.dayLabel}</div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="m-empty">Нет свободных слотов — можно перейти на страницу психолога и встать в лист ожидания.</div>
                    )}
                  </div>
                ) : (
                  <div className="mdates">
                    <div className="msl-label">Выберите дату</div>
                    {modalDates.map((row) => {
                      const rowLabel = `${row.dayLabel}, ${row.timeLabel}`;
                      const rowClass = spotsLeftClass(Number(row.spotsLeft || 0));
                      const selected = selectedModalDate === rowLabel;
                      const soldOut = Number(row.spotsLeft || 0) <= 0;
                      return (
                        <button
                          key={rowLabel}
                          type="button"
                          className={`mdr ${selected ? 'sel' : ''}`}
                          disabled={soldOut}
                          onClick={() => setSelectedModalDate(rowLabel)}
                        >
                          <div className="mdr-l">
                            <div className="mdr-day">{row.dayLabel}</div>
                            <div className="mdr-time">{row.timeLabel}</div>
                          </div>
                          <div className={`mdr-spots ${rowClass}`}>
                            {soldOut ? 'Занято' : `${row.spotsLeft} мест`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="mfoot">
                  <div className="mfprice">
                    {modalItem.priceLabel}
                    <small>{modalType === 'psych' ? 'за сессию' : 'за встречу'}</small>
                  </div>
                  <button type="button" className={`btn-promo ${modalFavoriteActive ? 'active' : ''}`} onClick={toggleModalFavorite}>
                    {modalFavoriteActive ? '✓ В избранном' : '+ В избранное'}
                  </button>
                  <Link className={`btn-mbook ${modalType === 'psych' ? 'mint' : 'lav'}`} to={modalDetailLink} onClick={closeModal}>
                    {modalType === 'psych' ? 'Открыть профиль →' : 'Открыть сессию →'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
